import { ChangeDetectionStrategy, Component, computed, input, model, signal } from "@angular/core";
import { DspProductAudienceType } from "@front/m19-api-client";
import { TranslocoDirective } from "@jsverse/transloco";
import {
  IButtonDirective,
  IInputGroupDirective,
  IInputGroupInput,
  IInputGroupSuffixDirective,
  ToggleButtonDirective,
  IIconComponent,
} from "@front/m19-ui";
import { FormsModule } from "@angular/forms";

interface LookbackSelection {
  lookback: number;
  isSelected: boolean;
  isDefault?: boolean;
}

@Component({
  selector: "app-dsp-audience-lookback-config",
  imports: [
    TranslocoDirective,
    ToggleButtonDirective,
    IButtonDirective,
    IInputGroupDirective,
    IInputGroupInput,
    FormsModule,
    IInputGroupSuffixDirective,
    IIconComponent,
  ],
  templateUrl: "./dsp-audience-lookback-config.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspAudienceLookbackConfigComponent {
  protected readonly maxLookbacks: Record<DspProductAudienceType, number> = {
    PRODUCT_PURCHASES: 365,
    PRODUCT_SEARCH: 90,
    PRODUCT_SIMS: 90,
    PRODUCT_VIEWS: 90,
  };

  audienceType = input.required<DspProductAudienceType>();

  selectedLookbacks = model.required<number[]>();

  isAddingCustomLookback = signal(false);
  lookbackBeingAdded = signal<number | undefined>(undefined);

  private customLookbacks = signal<number[]>([]);
  private defaultLookbacks = computed<number[]>(() => {
    const lookbacks = [7, 14, 30, 60, 90];
    if (this.audienceType() === DspProductAudienceType.PURCHASES) {
      lookbacks.push(365);
    }
    return lookbacks;
  });
  allLookbacks = computed<LookbackSelection[]>(() => {
    const defaultLookbacks = this.defaultLookbacks().map((lookback) => this.toLookbackSelection(lookback, true));
    const customLookbacks = this.customLookbacks().map((lookback) => this.toLookbackSelection(lookback));

    return [...defaultLookbacks, ...customLookbacks];
  });

  private toLookbackSelection(lookback: number, isDefault: boolean = false): LookbackSelection {
    return {
      lookback,
      isSelected: this.selectedLookbacks().includes(lookback),
      isDefault,
    };
  }

  toggleLookback(lookback: number, isChecked: boolean) {
    this.selectedLookbacks.update((lookbacks) => {
      if (isChecked) {
        lookbacks.push(lookback);
      } else {
        lookbacks = lookbacks.filter((l) => l !== lookback);
      }
      return [...lookbacks.sort((a, b) => a - b)];
    });
  }

  addCustomLookbackInput() {
    this.isAddingCustomLookback.set(true);
  }

  addCustomLookback() {
    const lookback = this.lookbackBeingAdded();

    this.lookbackBeingAdded.set(undefined);

    if (!lookback || lookback > this.maxLookbacks[this.audienceType()] || !this.isAddingCustomLookback()) {
      this.isAddingCustomLookback.set(false);
      return;
    }

    this.isAddingCustomLookback.set(false);

    if (this.allLookbacks().some((l) => l.lookback === lookback)) return;

    this.customLookbacks.update((lookbacks) => [...lookbacks, lookback]);
    this.selectedLookbacks.update((lookbacks) => [...lookbacks, lookback]);
  }

  removeLookback(lookback: number) {
    this.customLookbacks.update((lookbacks) => {
      lookbacks = lookbacks.filter((l) => l !== lookback);
      return [...lookbacks];
    });
    this.selectedLookbacks.update((lookbacks) => [...lookbacks.filter((l) => l !== lookback)]);
  }
}
